import React, { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  KeyRound,
  Send,
  Copy,
  CheckCircle2,
  XCircle,
  Search,
  Loader2,
  ShieldCheck,
  RefreshCw,
  Pencil,
  Clock,
} from "lucide-react";
import api from "@/lib/api";

export function CredentialsPanel({ onDataChange }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedMember, setSelectedMember] = useState(null);
  const [credStatus, setCredStatus] = useState(null);
  const [loadingCred, setLoadingCred] = useState(false);

  // Dialogs
  const [tempPwDialog, setTempPwDialog] = useState(null);
  const [resetMidDialog, setResetMidDialog] = useState(null);
  const [newMemberId, setNewMemberId] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const loadMembers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/applications/member", { params: { status: "approved", include_admins: true } });
      setMembers(data || []);
    } catch (err) {
      console.warn("[Credentials] Failed to load members:", err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadMembers(); }, [loadMembers]);

  const loadCredentialStatus = async (userId) => {
    setLoadingCred(true);
    try {
      const { data } = await api.get(`/admin/members/${userId}/credential-status`);
      setCredStatus(data);
    } catch (err) {
      toast.error("Failed to load credential status");
      console.warn("[Credentials] loadCredentialStatus:", err);
    }
    setLoadingCred(false);
  };

  const handleSelectMember = (m) => {
    setSelectedMember(m);
    setCredStatus(null);
    loadCredentialStatus(m.id);
  };

  const handleSendResetLink = async () => {
    if (!selectedMember) return;
    setActionLoading(true);
    try {
      const { data } = await api.post(`/admin/members/${selectedMember.id}/send-reset-link`);
      toast.success(data.message || "Reset link sent");
      onDataChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send reset link");
    }
    setActionLoading(false);
  };

  const handleGenerateTempPassword = async () => {
    if (!selectedMember) return;
    setActionLoading(true);
    try {
      const { data } = await api.post(`/admin/members/${selectedMember.id}/generate-temp-password`);
      setTempPwDialog(data);
      await loadCredentialStatus(selectedMember.id);
      onDataChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to generate password");
    }
    setActionLoading(false);
  };

  const handleResetMemberId = async () => {
    if (!selectedMember || !newMemberId.trim()) return;
    setActionLoading(true);
    try {
      const { data } = await api.post(`/admin/members/${selectedMember.id}/reset-member-id`, {
        member_id: newMemberId.trim(),
      });
      toast.success(`Member ID changed: ${data.old_member_id} → ${data.new_member_id}`);
      setResetMidDialog(null);
      setNewMemberId("");
      await loadMembers();
      await loadCredentialStatus(selectedMember.id);
      onDataChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update Member ID");
    }
    setActionLoading(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const filtered = members.filter((m) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    const name = `${m.first_name || ""} ${m.last_name || ""}`.toLowerCase();
    return name.includes(q) || (m.member_id || "").toLowerCase().includes(q);
  });

  return (
    <div className="space-y-4" data-testid="credentials-panel">
      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by name or Member ID…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 rounded-full"
          data-testid="credentials-search"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Member list */}
        <div className="lg:col-span-2">
          <Card className="rounded-2xl overflow-hidden">
            <div className="max-h-[520px] overflow-y-auto">
              <Table data-testid="credentials-member-table">
                <TableHeader className="sticky top-0 bg-[hsl(var(--secondary))]/80 backdrop-blur-sm z-10">
                  <TableRow>
                    <TableHead>Member</TableHead>
                    <TableHead>Member ID</TableHead>
                    <TableHead className="text-center">Password</TableHead>
                    <TableHead className="w-20"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-8">
                        <Loader2 className="h-5 w-5 mx-auto animate-spin text-muted-foreground" />
                      </TableCell>
                    </TableRow>
                  ) : filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                        No members found.
                      </TableCell>
                    </TableRow>
                  ) : filtered.map((m) => (
                    <TableRow
                      key={m.id}
                      className={`cursor-pointer transition-colors hover:bg-[hsl(var(--secondary))]/40 ${selectedMember?.id === m.id ? "bg-[hsl(var(--accent))]/10" : ""}`}
                      onClick={() => handleSelectMember(m)}
                      data-testid={`credentials-row-${m.id}`}
                    >
                      <TableCell className="font-medium">
                        {m.first_name} {m.last_name}
                      </TableCell>
                      <TableCell>
                        <code className="text-xs bg-[hsl(var(--secondary))] px-1.5 py-0.5 rounded">{m.member_id}</code>
                      </TableCell>
                      <TableCell className="text-center">
                        {m.has_password && !m.is_temp_password ? (
                          <Badge variant="outline" className="text-green-600 border-green-500/30 bg-green-500/10 gap-1 text-[10px] px-2 py-0.5">
                            <CheckCircle2 className="h-3 w-3" /> Set
                          </Badge>
                        ) : m.has_password && m.is_temp_password ? (
                          <Badge variant="outline" className="text-amber-600 border-amber-500/30 bg-amber-500/10 gap-1 text-[10px] px-2 py-0.5">
                            <Clock className="h-3 w-3" /> One-time
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-muted-foreground border-border gap-1 text-[10px] px-2 py-0.5">
                            <XCircle className="h-3 w-3" /> None
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 text-xs"
                          onClick={(e) => { e.stopPropagation(); handleSelectMember(m); }}
                        >
                          Manage
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </div>

        {/* Credential actions panel */}
        <div className="lg:col-span-1">
          {selectedMember ? (
            <Card className="rounded-2xl p-5 space-y-5" data-testid="credentials-actions">
              <div>
                <h3 className="text-base font-semibold tracking-tight">
                  {selectedMember.first_name} {selectedMember.last_name}
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Member ID: <strong>{credStatus?.member_id || selectedMember.member_id}</strong>
                </p>
              </div>

              {loadingCred ? (
                <div className="py-4 flex justify-center">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : credStatus && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Password status:</span>
                    {credStatus.has_password && !credStatus.is_temp_password ? (
                      <Badge variant="outline" className="text-green-600 border-green-500/30 bg-green-500/10 gap-1">
                        <CheckCircle2 className="h-3 w-3" /> Permanent password set
                      </Badge>
                    ) : credStatus.has_password && credStatus.is_temp_password ? (
                      <Badge variant="outline" className="text-amber-600 border-amber-500/30 bg-amber-500/10 gap-1">
                        <Clock className="h-3 w-3" /> One-time password (unused)
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-muted-foreground border-border gap-1">
                        <XCircle className="h-3 w-3" /> No password
                      </Badge>
                    )}
                  </div>
                  {credStatus.has_password && credStatus.is_temp_password && (
                    <p className="text-[11px] text-amber-600/80">
                      This member hasn't logged in yet to set their own password.
                    </p>
                  )}
                  {credStatus.password_set_at && (
                    <p className="text-xs text-muted-foreground">
                      Last set: {new Date(credStatus.password_set_at).toLocaleDateString("en-AU")}
                    </p>
                  )}
                </div>
              )}

              <div className="space-y-2.5">
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Actions</Label>

                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start rounded-lg gap-2 h-9"
                  onClick={handleSendResetLink}
                  disabled={actionLoading}
                  data-testid="send-reset-link-btn"
                >
                  <Send className="h-3.5 w-3.5" />
                  Send password reset link
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start rounded-lg gap-2 h-9"
                  onClick={handleGenerateTempPassword}
                  disabled={actionLoading}
                  data-testid="generate-temp-pw-btn"
                >
                  <KeyRound className="h-3.5 w-3.5" />
                  Generate temporary password
                </Button>
              </div>
            </Card>
          ) : null}
        </div>
      </div>

      {/* Temporary Password Dialog */}
      {tempPwDialog && (
        <Dialog open={!!tempPwDialog} onOpenChange={(o) => { if (!o) setTempPwDialog(null); }}>
          <DialogContent className="sm:max-w-sm rounded-2xl" data-testid="temp-pw-dialog">
            <DialogHeader>
              <DialogTitle>Temporary Password Generated</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="rounded-xl bg-[hsl(var(--secondary))] p-4 space-y-3 text-center">
                <p className="text-xs text-muted-foreground">For: <strong>{tempPwDialog.name}</strong></p>
                <p className="text-xs text-muted-foreground">Member ID</p>
                <p className="text-lg font-bold tracking-wider">{tempPwDialog.member_id}</p>
                <p className="text-xs text-muted-foreground mt-3">Temporary Password</p>
                <div className="flex items-center justify-center gap-2">
                  <code className="text-lg font-mono font-bold text-[hsl(var(--accent))]" data-testid="temp-password-value">
                    {tempPwDialog.temp_password}
                  </code>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 p-0"
                    onClick={() => copyToClipboard(tempPwDialog.temp_password)}
                    data-testid="copy-temp-pw-btn"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground text-center">
                Share this password with the member in person or via a secure channel. They can change it later from their profile.
              </p>
            </div>
            <DialogFooter>
              <Button onClick={() => setTempPwDialog(null)} className="rounded-full" data-testid="temp-pw-close-btn">
                Done
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Reset Member ID Dialog */}
      {resetMidDialog && (
        <Dialog open={!!resetMidDialog} onOpenChange={(o) => { if (!o) { setResetMidDialog(null); setNewMemberId(""); } }}>
          <DialogContent className="sm:max-w-sm rounded-2xl" data-testid="reset-mid-dialog">
            <DialogHeader>
              <DialogTitle>Change Member ID</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Change the login Member ID for <strong>{resetMidDialog.first_name} {resetMidDialog.last_name}</strong>.
              </p>
              <div className="space-y-2">
                <Label htmlFor="new-member-id">New Member ID</Label>
                <Input
                  id="new-member-id"
                  value={newMemberId}
                  onChange={(e) => setNewMemberId(e.target.value.toUpperCase())}
                  placeholder="0001"
                  data-testid="new-member-id-input"
                />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => { setResetMidDialog(null); setNewMemberId(""); }} className="rounded-full">
                Cancel
              </Button>
              <Button
                onClick={handleResetMemberId}
                disabled={actionLoading || !newMemberId.trim()}
                className="rounded-full bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white"
                data-testid="confirm-reset-mid-btn"
              >
                {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
